
import React,{useEffect,useState}from"react";
import{Box,ButtonBase,Paper,Typography,useMediaQuery}from"@mui/material";
import{useTheme}from"@mui/material/styles";
import{
  Inventory2Outlined,
  DescriptionOutlined,
  QueryStatsOutlined,
  ShoppingCartOutlined,
  ReceiptLongOutlined,
  RequestQuoteOutlined
}from"@mui/icons-material";
import{useNavigate}from"react-router-dom";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAuth}from"../../context/AuthContext";

const SIDEBAR_EVENT="yumim:sidebar-change";
const SIDEBAR_STATE_KEY="__yumimDesktopSidebarOpen";

function getDesktopSidebarOpen(){
  if(typeof window==="undefined")return true;

  if(typeof window[SIDEBAR_STATE_KEY]!=="boolean"){
    window[SIDEBAR_STATE_KEY]=true;
  }

  return window[SIDEBAR_STATE_KEY];
}

function updateDesktopSidebarOpen(value){
  if(typeof window==="undefined")return;

  const current=getDesktopSidebarOpen();
  const next=typeof value==="function"?Boolean(value(current)):Boolean(value);

  if(current===next)return;

  window[SIDEBAR_STATE_KEY]=next;
  window.dispatchEvent(new Event(SIDEBAR_EVENT));
}

function userCan(user,code){
  if(!user)return false;
  if(user.role==="super_admin"||user.role==="admin")return true;
  return(user.permissions||[]).includes(code);
}

const quickActions=[
  {
    title:"Nueva Factura",
    icon:RequestQuoteOutlined,
    path:"/admin-panel/facturacion",
    permission:"ventas.view",
    color:"#0891B2"
  },
  {
    title:"Documentos",
    icon:DescriptionOutlined,
    path:"/admin-panel/documentos",
    permission:"ventas.view",
    color:"#4F46E5"
  },
  {
    title:"Estadísticas",
    icon:QueryStatsOutlined,
    path:"/admin-panel/estadisticas",
    permission:"statistics.view",
    color:"#16A34A"
  },
  {
    title:"Productos",
    icon:Inventory2Outlined,
    path:"/admin-panel/productos",
    permission:"products.view",
    color:"#64748B"
  },
  {
    title:"Compras y Gastos",
    icon:ShoppingCartOutlined,
    path:"/admin-panel/compras",
    permission:"compras.view",
    color:"#F97316"
  },
  {
    title:"Órdenes",
    icon:ReceiptLongOutlined,
    path:"/admin-panel/ordenes",
    permission:"ventas.view",
    color:"#DC2626"
  }
];

export default function AdminPanel(){
  const muiTheme=useTheme();
  const isMobile=useMediaQuery(muiTheme.breakpoints.down("md"));
  const[desktopOpen,setDesktopOpen]=useState(getDesktopSidebarOpen);
  const[mobileOpen,setMobileOpen]=useState(false);
  const{dashboardTheme}=useDashboardTheme();
  const{user}=useAuth();
  const navigate=useNavigate();

  useEffect(()=>{
    const syncSidebar=()=>{
      setDesktopOpen(getDesktopSidebarOpen());
    };

    window.addEventListener(SIDEBAR_EVENT,syncSidebar);
    syncSidebar();

    return()=>{
      window.removeEventListener(SIDEBAR_EVENT,syncSidebar);
    };
  },[]);

  const sidebarOpen=isMobile?mobileOpen:desktopOpen;

  const handleToggleSidebar=()=>{
    if(isMobile){
      setMobileOpen(previous=>!previous);
      return;
    }

    updateDesktopSidebarOpen(previous=>!previous);
  };

  const handleCloseSidebar=()=>{
    if(isMobile){
      setMobileOpen(false);
      return;
    }

    updateDesktopSidebarOpen(false);
  };

  const items=quickActions.filter(item=>userCan(user,item.permission));

  const displayName=
    user?.name?.trim()||
    user?.email?.split("@")[0]||
    "Usuario";

  const background=dashboardTheme.dashboard_background;
  const paper=dashboardTheme.card_background;
  const text=dashboardTheme.text_color;
  const muted=dashboardTheme.muted_text_color;
  const border=dashboardTheme.border_color;
  const primary=dashboardTheme.primary_color;
  const sidebarColor=dashboardTheme.sidebar_color;
  const sidebarText=dashboardTheme.sidebar_text_color;

  return(
    <Box
      sx={{
        minHeight:"100dvh",
        width:"100%",
        display:"flex",
        bgcolor:background,
        color:text,
        overflowX:"hidden"
      }}
    >
      <Sidebar
        open={sidebarOpen}
        onClose={handleCloseSidebar}
      />

      <Box
        sx={{
          flex:1,
          minWidth:0,
          minHeight:"100dvh",
          display:"flex",
          flexDirection:"column"
        }}
      >
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={handleToggleSidebar}
        />

        <Box
          component="main"
          sx={{
            flex:1,
            minWidth:0,
            width:"100%",
            boxSizing:"border-box",
            px:{xs:1.5,sm:2.5,lg:3},
            py:{xs:2,sm:2.5,lg:3},
            display:"flex",
            flexDirection:"column",
            gap:2.5
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position:"relative",
              width:"100%",
              minWidth:0,
              boxSizing:"border-box",
              overflow:"hidden",
              bgcolor:sidebarColor,
              color:sidebarText,
              borderRadius:3,
              px:{xs:2.5,sm:3.5,md:4},
              py:{xs:3,sm:3.5,md:4},
              boxShadow:"none"
            }}
          >
            <Box
              sx={{
                position:"relative",
                display:"flex",
                flexDirection:"column",
                alignItems:{xs:"center",sm:"flex-start"},
                textAlign:{xs:"center",sm:"left"}
              }}
            >
              <Typography
                sx={{
                  fontSize:{xs:12,sm:13.5},
                  fontWeight:400,
                  color:"inherit",
                  opacity:0.85,
                  mb:1
                }}
              >
                ¡Sonríe, hay postre!
              </Typography>

              <Typography
                component="h1"
                sx={{
                  fontSize:{xs:23,sm:28,md:31},
                  fontWeight:700,
                  lineHeight:1.3,
                  color:"inherit",
                  overflowWrap:"anywhere"
                }}
              >
                ¡Bienvenido, {displayName}!
              </Typography>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              width:"100%",
              minWidth:0,
              boxSizing:"border-box",
              border:`1px solid ${border}`,
              bgcolor:paper,
              borderRadius:3,
              p:{xs:2.2,sm:2.75},
              boxShadow:"none"
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize:{xs:20.9,sm:24.2},
                fontWeight:700,
                color:primary,
                mb:2.2
              }}
            >
              Accesos Rápidos
            </Typography>

            <Box
              sx={{
                display:"flex",
                flexWrap:"wrap",
                alignItems:"stretch",
                justifyContent:"center",
                gap:{xs:1.1,sm:1.65}
              }}
            >
              {items.map(item=>{
                const Icon=item.icon;

                return(
                  <ButtonBase
                    key={item.path}
                    onClick={()=>navigate(item.path)}
                    aria-label={`Ir a ${item.title}`}
                    sx={{
                      width:{
                        xs:"calc((100% - 8.8px) / 2)",
                        sm:"calc((100% - 26.4px) / 3)",
                        lg:"calc((100% - 39.6px) / 4)"
                      },
                      flexGrow:0,
                      flexShrink:0,
                      minWidth:0,
                      minHeight:{xs:123,sm:132},
                      py:2.2,
                      px:1.1,
                      border:`1px solid ${border}`,
                      borderRadius:2.75,
                      bgcolor:paper,
                      display:"flex",
                      flexDirection:"column",
                      alignItems:"center",
                      justifyContent:"center",
                      gap:1.32,
                      boxShadow:"none",
                      transition:"border-color .2s ease,background-color .2s ease",
                      "&:hover":{
                        borderColor:primary,
                        bgcolor:background
                      },
                      "&:focus-visible":{
                        outline:`2px solid ${primary}`,
                        outlineOffset:2
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width:{xs:52,sm:57},
                        height:{xs:52,sm:57},
                        borderRadius:"50%",
                        bgcolor:item.color,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"center",
                        flexShrink:0
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize:{xs:25,sm:28},
                          color:"#FFFFFF"
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontSize:{xs:13.5,sm:14.5},
                        fontWeight:600,
                        lineHeight:1.3,
                        textAlign:"center",
                        color:text,
                        overflowWrap:"anywhere"
                      }}
                    >
                      {item.title}
                    </Typography>
                  </ButtonBase>
                );
              })}
            </Box>

            {items.length===0&&(
              <Typography
                sx={{
                  fontSize:14,
                  fontWeight:400,
                  color:muted,
                  textAlign:"center",
                  py:3
                }}
              >
                No tienes accesos disponibles.
              </Typography>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
